import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GetMarkets200Response,
  InstrumentTypeResponse,
  OptionTypeResponse,
} from "../../codegen-api";
import { IOptionMarket } from "./useGetMarkets";
import { IMarket } from "../MarketContext";
import { SELECTED_EXPIRY_OVERRIDE } from "./constants";

export function useOptionSelection(
  market: IMarket,
  marketsData: GetMarkets200Response[] | undefined,
  expiries: number[]
) {
  const [selectedStrike, setStrike] = useState<string>();
  const [selectedExpiry, setExpiry] = useState<number>();
  const [selectedOrderType, setOrderType] = useState<OptionTypeResponse>();

  const orderTypes = useMemo(() => {
    const filteredOptions =
      marketsData?.filter((v) =>
        selectedExpiry ? Number(v.expiry) === selectedExpiry : true
      ) || [];

    const types: OptionTypeResponse[] = [];
    const hasCall = filteredOptions.some(
      (o) => o.option_type === OptionTypeResponse.Call
    );
    const hasPut = filteredOptions.some(
      (o) => o.option_type === OptionTypeResponse.Put
    );

    if (hasCall) {
      types.push(OptionTypeResponse.Call);
    }
    if (hasPut) {
      types.push(OptionTypeResponse.Put);
    }

    return types;
  }, [marketsData, selectedExpiry]);

  const strikes = useMemo(() => {
    if (market.derivative === InstrumentTypeResponse.Option) {
      // Filter instruments from selectedOrderType if available
      const filteredOptions =
        marketsData?.filter(
          (v) =>
            (selectedOrderType ? v.option_type === selectedOrderType : true) &&
            (selectedExpiry ? Number(v.expiry) === selectedExpiry : true)
        ) || [];

      const uniqueStrikes = Array.from(
        new Set(filteredOptions.map(({ strike }) => strike!) || [])
      );
      return uniqueStrikes.sort((a, b) => Number(a) - Number(b));
    }
    return [];
  }, [market.derivative, marketsData, selectedExpiry, selectedOrderType]);

  // Return the selected values, or if editing order exists, return that instead
  const selectedValues = useMemo(
    () => ({
      strike: selectedStrike,
      expiry: selectedExpiry,
      orderType: selectedOrderType,
    }),
    [selectedExpiry, selectedOrderType, selectedStrike]
  );

  const selectedOptionInstrument = useMemo(() => {
    if (market.derivative === InstrumentTypeResponse.Option) {
      const { strike, expiry, orderType } = selectedValues;

      // Find the instrument using the selected values.
      if (!strike || !expiry || !orderType) {
        return undefined;
      }

      return marketsData?.find(
        (v) =>
          Number(v.expiry) === expiry &&
          v.option_type === orderType &&
          v.strike === strike
      ) as IOptionMarket;
    }
    return undefined;
  }, [market.derivative, marketsData, selectedValues]);

  const selectedPerpetualInstrument = useMemo(() => {
    if (market.derivative === InstrumentTypeResponse.Perpetual) {
      // Find the perpetual market that matches the current asset
      const found = marketsData?.find(
        (m) => m.underlying_asset === market.asset
      );
      const result = found || marketsData?.[0];

      return result;
    }
    return undefined;
  }, [market.derivative, market.asset, marketsData]);

  const selectedSpotInstrument = useMemo(() => {
    if (market.derivative === InstrumentTypeResponse.Spot) {
      const found = marketsData?.find(
        (m) =>
          m.underlying_asset === market.asset &&
          m.instrument_type === InstrumentTypeResponse.Spot
      );
      return found || marketsData?.find(
        (m) => m.underlying_asset === market.asset
      ) || marketsData?.[0];
    }
    return undefined;
  }, [market.derivative, market.asset, marketsData]);

  const reset = useCallback(() => {
    setStrike(undefined);
    setExpiry(undefined);
    setOrderType(undefined);
  }, []);

  useEffect(() => {
    if (selectedOrderType && !orderTypes.includes(selectedOrderType)) {
      setOrderType(orderTypes[0]);
    }
  }, [selectedOrderType, orderTypes]);

  // Whenever selectedStrike changes and its not included in strikes,
  // remove selected strike
  useEffect(() => {
    if (selectedStrike && !strikes.includes(selectedStrike)) {
      setStrike(undefined);
    }
  }, [selectedStrike, strikes]);

  // When there are existing expiries but none is selected, default to the first
  useEffect(() => {
    setExpiry((prev) => {
      if (!!prev || !expiries.length) {
        return prev;
      }
      return expiries[SELECTED_EXPIRY_OVERRIDE];
    });
  }, [expiries]);

  return {
    setStrike,
    setExpiry,
    setOrderType,
    orderTypes,
    strikes,
    selectedValues,
    selectedOptionInstrument,
    selectedPerpetualInstrument,
    selectedSpotInstrument,
    reset,
  };
}
